import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { preloadImage } from '@theme/utilities';

/**
 * @param {Node | undefined} node
 * @returns {string | number | undefined}
 */
function getMorphKey(node) {
  if (!(node instanceof HTMLElement)) return undefined;

  if (node.tagName === 'SLIDESHOW-SLIDE') {
    const slideId = node.getAttribute('slide-id');
    if (slideId) return `slide-${slideId}`;
  }

  if (node.dataset.morphKey) return node.dataset.morphKey;

  const mediaId = node.getAttribute('data-media-id');
  if (mediaId) return `media-${mediaId}`;

  if (node.id?.startsWith('gallery-media-')) return node.id;

  const thumbnailAction = node.getAttribute('on:click');
  if (thumbnailAction?.includes('/select') || thumbnailAction?.startsWith('/handleThumbnailClick/')) {
    return `control-${thumbnailAction}`;
  }

  return undefined;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {Set<string>} */
  #preloadedUrls = new Set();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#scheduleGalleryPreload();
    this.#setupVariantHoverPreload();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by morphing the gallery in place.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (event.detail.data.newProduct) {
      this.replaceWith(newMediaGallery);
      return;
    }

    this.#preloadFromVariantResource(event.detail.resource);

    morph(this, newMediaGallery, {
      ...MORPH_OPTIONS,
      childrenOnly: true,
      getNodeKey: getMorphKey,
    });

    this.updatedCallback();

    requestAnimationFrame(() => {
      const slideshow = this.slideshow;

      if (slideshow) {
        // The morph brings in the authoritative markup for the new variant (arrows, controls,
        // single-media class, slide order). Refresh the slideshow's refs against that DOM and
        // rebuild it so the Scroller/observers match the now-visible slide set. Without this,
        // switching from a single-image variant (where the Scroller was never created) to a
        // multi-image one leaves select() bailing on a null Scroller - controls highlight but
        // the slides never move. See Slideshow.reinitialize.
        slideshow.updatedCallback();
        slideshow.reinitialize();
        slideshow.select(0, undefined, { animate: false });
      }

      this.refs.zoomDialogComponent?.updatedCallback?.();
    });
  };

  /**
   * Instantly swaps the visible gallery media to match the newly selected color/option
   * group, without waiting for the variant-picker's section fetch to resolve. This is
   * what makes switching variants feel instant instead of taking 1-3s.
   *
   * Relies on `data-gallery-group` + `hidden` attributes rendered server-side in
   * snippets/product-media-slide.liquid and snippets/product-media-grid-item.liquid,
   * which are only present when the "Hide unselected variant media" block setting is
   * enabled. Every color's media is pre-rendered in the DOM; non-selected colors are
   * simply hidden. If the changed option isn't a gallery-grouping option (e.g. Size),
   * no matching `data-gallery-group` element will be found and this is a no-op - the
   * gallery is left untouched, which is correct since the color didn't change.
   *
   * The regular #handleVariantUpdate flow still runs afterwards once the fetch
   * resolves, morphing in the authoritative server-rendered markup - this is purely
   * an optimistic, instant preview.
   *
   * @param {VariantSelectedEvent} event
   */
  #handleVariantSelected = (event) => {
    const groupId = event.detail?.resource?.id;
    if (!groupId) return;

    const groupedElements = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-gallery-group]'));
    if (!groupedElements.length) return;

    const hasMatch = Array.from(groupedElements).some((element) => element.dataset.galleryGroup === groupId);
    if (!hasMatch) return;

    /** @type {string | undefined} */
    let firstVisibleSlideId;

    for (const element of groupedElements) {
      if (element.dataset.galleryGroup === groupId) {
        element.removeAttribute('hidden');

        if (firstVisibleSlideId === undefined && element.tagName === 'SLIDESHOW-SLIDE') {
          firstVisibleSlideId = element.getAttribute('slide-id') ?? undefined;
        }
      } else {
        element.setAttribute('hidden', '');
      }
    }

    // Rebuild the slideshow against the newly visible slide set. Without this, switching
    // from a single-image group (where the Scroller was never created) to a multi-image
    // group leaves the track inert - controls highlight but slides don't move. It also
    // re-points the visibility observer at the now-visible slides. See Slideshow.reinitialize.
    this.slideshow?.reinitialize();

    if (firstVisibleSlideId) {
      this.slideshow?.select({ id: firstVisibleSlideId }, undefined, { animate: false });
    }
  };

  #scheduleGalleryPreload() {
    const run = () => this.#preloadGalleryImages();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 2500 });
    } else {
      setTimeout(run, 300);
    }
  }

  #preloadGalleryImages() {
    const script = this.querySelector('[data-variant-media-preload]');
    if (!script?.textContent?.trim()) return;

    try {
      const urls = JSON.parse(script.textContent);

      if (!Array.isArray(urls)) return;

      for (const url of urls) {
        this.#preloadUrl(url);
      }
    } catch {
      // Ignore invalid preload JSON.
    }
  }

  /**
   * @param {string | undefined | null} url
   */
  #preloadUrl(url) {
    if (!url || this.#preloadedUrls.has(url)) return;

    preloadImage(url);
    this.#preloadedUrls.add(url);
  }

  /**
   * @param {VariantUpdateEvent['detail']['resource']} resource
   */
  #preloadFromVariantResource(resource) {
    const featuredSrc = resource?.featured_media?.preview_image?.src;
    if (featuredSrc) this.#preloadUrl(featuredSrc);
  }

  #setupVariantHoverPreload() {
    const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (!variantPicker) return;

    variantPicker.addEventListener(
      'pointerenter',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const input = target.closest('input[data-featured-media-url]');
        this.#preloadUrl(input?.getAttribute('data-featured-media-url'));
      },
      { capture: true, signal: this.#controller.signal }
    );
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
